import type { InfiniteData, QueryFunction, QueryKey } from '@tanstack/query-core';
import {
  type QueryFunctionContext,
  type SkipToken,
  type UndefinedInitialDataInfiniteOptions,
  type UseMutationOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { createContext } from 'react';
import type { FuuzeError } from '../models/fuuze-error.js';
import type { PaginateResult } from '../models/paginate-result.js';
import type { FetchService } from '../services/fetch.service.js';
import { sleep } from '../utils/time.util.js';
import type { RequiredFields } from '../utils/typescript.util.js';

let DELAY_TO_DISPLAY_LOADING = 200;

/**
 * Set the global delay to display loading for each query.
 * This is useful to avoid flickering when the data is fetched too fast.
 * This is the minimum time to wait before receiving the data.
 * @param delay
 */
export function setDelayToDisplayLoading(delay: number) {
  DELAY_TO_DISPLAY_LOADING = delay;
}

export type FuuzeQueryKey = [
  string,
  {
    type: 'user' | 'podcastChannel' | 'podcastEpisode' | 'comment' | 'config' | 'spotlight' | 'noType';
  },
  ...any,
];

export const QueriesContext = createContext<FetchService>({} as any);

export type ObjectOrId<T> = string | (T & { _id: string });

export type FuuzeUndefinedInitialDataInfiniteOptions<
  TQueryFnData,
  TData = InfiniteData<TQueryFnData>,
  TPageParam = number,
> = Partial<UndefinedInitialDataInfiniteOptions<TQueryFnData, FuuzeError, TData, QueryKey, TPageParam>> & {
  delayToDisplayLoading?: number;
  // queryKey: QueryKey
};

export type FuuzeQueryOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, FuuzeError, TData>,
  'queryKey'
> & {
  delayToDisplayLoading?: number;
  // queryKey: QueryKey
};

export type FuuzeMutationOptions<TData, TVariables = void, TContext = unknown> = UseMutationOptions<
  TData,
  FuuzeError,
  TVariables,
  TContext
>;

export function useFuuzeQuery<TQueryFnData = unknown, TData = TQueryFnData>(
  options: RequiredFields<FuuzeQueryOptions<TQueryFnData, TData>, 'queryFn'> & { queryKey: FuuzeQueryKey },
) {
  return useQuery({
    ...options,
    queryFn: context => _fuuzeQueryFn(options?.queryFn, context, options?.delayToDisplayLoading),
  });
}

export function useFuuzeInfiniteQuery<TQueryFnData, TData = InfiniteData<TQueryFnData>>(
  options: RequiredFields<
    FuuzeUndefinedInitialDataInfiniteOptions<TQueryFnData, TData>,
    'getNextPageParam' | 'queryKey' | 'queryFn'
  >,
) {
  return useInfiniteQuery({
    ...options,
    initialPageParam: options.initialPageParam || 1,
    queryFn: context => _fuuzeQueryFn(options.queryFn, context, options.delayToDisplayLoading),
  });
}

export async function _fuuzeQueryFn<TQueryFnData>(
  originalQueryFn: QueryFunction<TQueryFnData> | SkipToken,
  context: QueryFunctionContext,
  delayToDisplayLoading = DELAY_TO_DISPLAY_LOADING,
): Promise<TQueryFnData> {
  if (typeof originalQueryFn === 'symbol') return null as any;
  const [data] = await Promise.allSettled([originalQueryFn(context), sleep(delayToDisplayLoading)]);
  if (data.status === 'rejected') throw data.reason as FuuzeError;
  return data.value;
}

export function generateInfinitePaginatedResult<T, TPageParam = number>({
  length,
  generator,
}: {
  length: number;
  generator: () => T;
}): InfiniteData<PaginateResult<T>, TPageParam> {
  return {
    pageParams: [],
    pages: [
      {
        docs: Array.from({ length }).map(generator),
        totalDocs: 0,
        limit: 0,
        hasPrevPage: false,
        hasNextPage: false,
        totalPages: 0,
        offset: 0,
        pagingCounter: 0,
      },
    ],
  };
}

export function buildUrlSearchParams(obj: object) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      params.set(key, String(value));
    }
  }
  return params;
}

export function networkUtil(fetchService: FetchService, idToken?: string) {
  async function getProxyImageUrl(src?: string) {
    if (!src) return undefined;
    return `${fetchService.getBaseUrl()}/image-proxy?${buildUrlSearchParams({ url: src, token: idToken })}`;
  }

  return {
    getProxyImageUrl,
  };
}
